import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  forwardRef,
  Input,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { SelectData } from '../../type/input';
import { CommonModule } from '@angular/common';
import { IonicModule, PopoverController } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { ValidationError } from '../../type/validators';
import { SingleSpaceDirective } from '../../directives/single-space.directive';
import { CustomSelectPopoverComponent } from '../custom-select-popover/custom-select-popover.component';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    ReactiveFormsModule,
    TranslateModule,
    SingleSpaceDirective,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
})
export class InputComponent implements ControlValueAccessor, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() formControlName!: string;
  @Input() label: string = '';
  @Input() displayValue: string = '';
  @Input() type:
    | 'text'
    | 'password'
    | 'passwordText'
    | 'select'
    | 'selectwithsearch'
    | 'text-area'
    | 'email' = 'text';
  @Input() options!: SelectData[];
  @Input() required: boolean = false;
  @Input() maxLength: number | null = null;
  @Input() colSize: string = '12';
  @Input() colSizeMd: string = '6';
  @Input() tootipInfo!: string;
  @Input() defaultValueSelect!: { value: string; label: string };
  @Input() doFocus!: boolean;
  @ViewChild('inputElement', { read: ElementRef }) inputElement?: ElementRef<HTMLIonInputElement>;
  touched: boolean = false;

  value: any;

  constructor(private popoverCtrl: PopoverController) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['doFocus'] && changes['doFocus'].currentValue === true) {
      setTimeout(async () => {
        await this.setFocus();
      }, 100);
    }
  }

 async setFocus(): Promise<void> {
    if (this.inputElement?.nativeElement) {
      const ionInput = this.inputElement.nativeElement;
      try {
        await ionInput.setFocus();
      } catch (error) {
        console.error('Error setting focus:', error);
      }
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onChange(value: any) {}
  onTouched() {}

  get formControl(): FormControl {
    return this.formGroup.get(this.formControlName) as FormControl;
  }

  onFocusInput() {
    if (this.touched) {
      this.formControl.markAsDirty();
      this.formControl.markAsTouched();
    }
  }
  onBlurInput() {
    if (this.formControl && !this.formControl.value && !this.touched) {
      this.formControl.markAsPristine();
      this.formControl.markAsUntouched();
      this.touched = true;
    }
  }

  onInputChange() {
    if (this.formControlName === 'password') {
      const validationPasswordControl = this.formGroup.get(
        'validation_password'
      );

      if (validationPasswordControl?.value) {
        if (!validationPasswordControl.errors) {
          validationPasswordControl.markAsDirty();
          validationPasswordControl.markAsTouched();
        }
      }
    }
    if (this.formControlName === 'email') {
      const validationMailControl = this.formGroup.get('validation_email');

      if (validationMailControl?.value) {
        if (!validationMailControl.errors) {
          validationMailControl.setErrors(null);
          validationMailControl.markAsTouched();
        }
      }
    }
  }

  get error(): ValidationError | null {
    const errors: ValidationError = this.formGroup.controls[
      this.formControlName
    ].errors as ValidationError;

    if (this.formControl.touched && errors) {
      return errors;
    }

    return null;
  }

  blockCopyCutPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  blockCopyPaste(event: KeyboardEvent): void {
    if (
      (event.ctrlKey || event.metaKey) &&
      (event.key === 'v' || event.key === 'c' || event.key === 'x')
    ) {
      event.preventDefault();
    }
  }

  async openCustomPopover(event: any) {
    const popover = await this.popoverCtrl.create({
      component: CustomSelectPopoverComponent,
      event,
      translucent: true,
      cssClass: 'custom-select-popover',
      backdropDismiss: true,
      showBackdrop: false,
      size: 'cover',
      side: 'bottom',
      alignment: 'center',
      arrow: true,
      keyboardClose: true,
      componentProps: {
        options: this.options,
        defaultValue: this.defaultValueSelect,
      },
    });

    await popover.present();

    const { data } = await popover.onDidDismiss();
    if (data !== undefined) {
      this.formGroup.get(this.formControlName)?.setValue(data);
      this.formGroup.get(this.formControlName)?.markAsDirty();
      this.formGroup.get(this.formControlName)?.markAsTouched();
      this.formGroup.get(this.formControlName)?.updateValueAndValidity();
    } else {
      this.formGroup.get(this.formControlName)?.markAsDirty();
      this.formGroup.get(this.formControlName)?.markAsTouched();
      this.formGroup.get(this.formControlName)?.updateValueAndValidity();
    }
  }
}
